import * as React from 'react';
import { cx } from 'linaria';
import { TreeNodeProps } from './tree.types';
import * as styles from './tree.styles';
import { flattenReactChildren } from '../utils/flatten-react-children';

import { IconAdd, IconChevronRight } from '../foundation/icons';
import { TreeContext } from './tree.context';
import { useFocusManager } from '../focus-scope';
import { composeEventHandlers } from '../utils/compose-event-handlers';

export const TreeNode = React.forwardRef<HTMLDivElement, React.PropsWithChildren<TreeNodeProps>>((props, ref) => {
    const { children, label, level, nodeId, onNodeClick, onActionClick, actionIcon } = props;
    const [isMouseOver, setIsMouseOver] = React.useState<boolean>(false);

    const treeContext = React.useContext(TreeContext);
    const isOpen = treeContext.checkIsNodeVisible(nodeId);
    const isSelected = treeContext.checkIsNodeSelected(nodeId);

    const flatChildren = flattenReactChildren(children);
    const hasChildren = flatChildren && flatChildren.length > 0;

    const focusManager = useFocusManager();

    const onKeyDown = React.useCallback(
        (e: React.KeyboardEvent) => {
            switch (e.key) {
                case 'ArrowDown':
                    e.preventDefault();
                    focusManager.focusNext({ wrap: true });

                    break;
                case 'ArrowUp':
                    e.preventDefault();
                    focusManager.focusPrevious({ wrap: true });

                    break;
            }
        },
        [focusManager],
    );

    const getTriggerProps = React.useCallback(
        ({ className, onKeyDown: customOnKeyDown, ...passThroughProps } = {}) => {
            return treeContext.getTriggerProps({
                type: 'button',
                disabled: false,
                nodeId,
                className: cx(isSelected ? styles.triggerButtonSelected : styles.triggerButton, className),
                onClick: () => onNodeClick?.(nodeId),
                onKeyDown: composeEventHandlers(customOnKeyDown, onKeyDown),
                ...passThroughProps,
            });
        },
        [onKeyDown, nodeId, treeContext],
    );

    const nodeProps = treeContext.getNodeProps({
        nodeId: nodeId,
    });

    return (
        <li className={cx(styles.rootNodeStyle)}>
            <div ref={ref} {...nodeProps}>
                <button {...getTriggerProps()}>
                    <div
                        className={cx(styles.treeNodeStyle, isSelected && styles.treeNodeSelectedStyle)}
                        style={{ paddingLeft: `${level === 1 ? '16' : level * 14}px` }}
                        onMouseEnter={() => setIsMouseOver(true)}
                        onMouseLeave={() => setIsMouseOver(false)}
                    >
                        {hasChildren && (
                            <IconChevronRight className={cx(styles.triggerArrowIcon, isOpen && styles.rotateIcon)} />
                        )}
                        <label className={cx(styles.nodeLabelStyle, !hasChildren && styles.nodeLabelNoIcon)}>
                            <span>{label}</span>
                        </label>
                        {onActionClick && (
                            <div
                                data-testid={`${nodeId}-action-button`}
                                className={cx(isMouseOver ? styles.actionButton : styles.actionButtonHidden)}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onActionClick(nodeId);
                                }}
                            >
                                {actionIcon ? <>{actionIcon}</> : <IconAdd className={cx(styles.rightActionIcon)} />}
                            </div>
                        )}
                    </div>
                </button>
            </div>

            {isOpen && hasChildren && (
                <div>
                    <ul>
                        {React.Children.map(flatChildren, (child, index) => {
                            return React.cloneElement(child as React.ReactElement, {
                                nodeId: `${nodeId}.${index}`,
                                currentIndex: index,
                                level: level + 1,
                            });
                        })}
                    </ul>
                </div>
            )}
        </li>
    );
});

TreeNode.displayName = 'Tree.Node';
